/** @jsxImportSource @emotion/react */
import React from "react";
import { Form, Table, Empty, Grid } from "antd";
import { closestCenter, DndContext, DragOverlay } from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  restrictToHorizontalAxis,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { ColumnFilterItem } from "antd/lib/table/interface";
import { DataTableProps, ThumbnailSize } from "./types";
import {
  createTableBodyCell,
  DragIndexContext,
  rowDragColumn,
  SortableRow,
  TableHeaderCell,
  useColumnDnd,
  useRowDnd,
} from "./Dnd";
import { buildColumns } from "./ColumnBuilder";
import { createColumnFilters } from "./filters";
import * as Styles from "./styles";

const thumbnailSizePx = (size: ThumbnailSize) => {
  if (size === "small") {
    return 50;
  }
  if (size === "medium") {
    return 100;
  }
  return 200;
};

// eslint-disable-next-line @typescript-eslint/ban-types
const EditableColumnTableComponent = <T extends object>({
  rowKey,
  dataSource,
  columnConfigs,
  loading,
  columnToggle = false,
  editable = false,
  pagination = false,
  thumbnailSize = "medium",
  visibility,
  edit,
  rowDnd = false,
  onRowReorder,
  rowClassName,
}: DataTableProps<T>) => {
  const [order, setOrder] = React.useState<string[]>(() =>
    columnConfigs.map((c) => c.key),
  );

  const { sensors, dragIndex, onDragEnd, onDragOver } = useColumnDnd(
    order,
    setOrder,
  );

  const screens = Grid.useBreakpoint();

  const filters = React.useMemo<Record<string, ColumnFilterItem[]>>(() => {
    const result: Record<string, ColumnFilterItem[]> = {};
    columnConfigs
      .filter((config) => config.filterable)
      .forEach((config) => {
        result[config.key] = createColumnFilters(
          dataSource as Array<Record<string, unknown>>,
          config.dataIndex ?? config.key,
        );
      });
    return result;
  }, [columnConfigs, dataSource]);

  const columns = React.useMemo(
    () =>
      buildColumns<T>(columnConfigs, {
        order,
        visibility: columnToggle ? visibility?.visibility ?? {} : {},
        filters,
        thumbnailSize: thumbnailSizePx(thumbnailSize),
        editing: editable ? edit?.isEditing ?? false : false,
      }),
    [
      columnConfigs,
      order,
      columnToggle,
      visibility?.visibility,
      filters,
      thumbnailSize,
      editable,
      edit?.isEditing,
    ],
  );

  const bodyCell = React.useMemo(() => createTableBodyCell(rowKey), [rowKey]);

  const hasVisibleColumns = columns.length > 0;

  const tableColumns = hasVisibleColumns
    ? columns
    : [
        {
          key: "empty-placeholder",
          title: "No columns selected",
          onHeaderCell: () => ({ id: "empty-placeholder" }),
          render: () => null,
        },
      ];

  const tableDataSource = hasVisibleColumns ? dataSource : [];

  const rowKeys = (tableDataSource as Array<Record<string, unknown>>).map(
    (row) => String(row[rowKey]),
  );

  const { sensors: rowSensors, onDragEnd: onRowDragEnd } = useRowDnd(
    rowKeys,
    onRowReorder,
  );

  const tableLocale = hasVisibleColumns
    ? undefined
    : {
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Please select at least one column to display"
          />
        ),
      };

  const activeTitle = columnConfigs.find(
    (c) => c.key === dragIndex.active,
  )?.title;

  if (rowDnd) {
    return (
      <DndContext
        sensors={rowSensors}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={onRowDragEnd}
        collisionDetection={closestCenter}
      >
        <SortableContext items={rowKeys} strategy={verticalListSortingStrategy}>
          <Form
            form={edit?.form}
            component={false}
            onValuesChange={edit?.onFormValueChanges}
          >
            <Table<T>
              rowKey={rowKey}
              loading={loading}
              dataSource={tableDataSource}
              locale={tableLocale}
              css={Styles.tableStyle}
              rowClassName={rowClassName}
              pagination={pagination ? { position: ["topRight"] } : false}
              scroll={{ x: "max-content" }}
              columns={[rowDragColumn, ...tableColumns]}
              components={{
                body: { cell: bodyCell, row: SortableRow },
              }}
            ></Table>
          </Form>
        </SortableContext>
      </DndContext>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext items={order} strategy={horizontalListSortingStrategy}>
          <DragIndexContext.Provider value={dragIndex}>
            <Form
              form={edit?.form}
              component={false}
              onValuesChange={edit?.onFormValueChanges}
            >
              <Table<T>
                rowKey={rowKey}
                loading={loading}
                dataSource={tableDataSource}
                locale={tableLocale}
                css={Styles.tableStyle}
                rowClassName={rowClassName}
                pagination={
                  pagination
                    ? {
                        position: ["topRight"],
                        disabled: editable && (edit?.isEditing ?? false),
                      }
                    : false
                }
                scroll={{
                  x: "max-content",
                  y: screens.lg === false ? undefined : "calc(100vh - 381px)",
                }}
                rowSelection={
                  editable && hasVisibleColumns
                    ? edit?.rowSelection() || undefined
                    : undefined
                }
                columns={tableColumns}
                components={{
                  header: { cell: TableHeaderCell },
                  body: { cell: bodyCell },
                }}
              ></Table>
            </Form>
          </DragIndexContext.Provider>
        </SortableContext>
        <DragOverlay>
          {activeTitle ? (
            <th
              style={{
                backgroundColor: "#E7E7E7",
                padding: 16,
                width: 300,
                fontWeight: "bold",
                textAlign: "left",
              }}
            >
              {activeTitle}
            </th>
          ) : null}
        </DragOverlay>
      </DndContext>
    </>
  );
};

export const DataTable = React.memo(
  EditableColumnTableComponent,
  // eslint-disable-next-line @typescript-eslint/ban-types
) as unknown as <T extends object>(
  props: DataTableProps<T>,
) => React.ReactElement;
